import { useCallback } from 'react';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import type { QueryClient } from '@tanstack/react-query';

import {
  getRegimes,
  createRegime,
  getRegimeGroups,
  createRegimeGroup,
  updateRegimeGroup,
  deleteRegimeGroup,
  updateRegime,
  deleteRegime,
  copyRegime,
  validateRegimeGroup,
} from 'api/aid-models/aid-regimes.api';
import { getCustomLogic } from 'api/aid-models/aid-custom-logic.api';
import { getAcademicRanks } from 'api/generated/aid-simulation-academic-ranks/aid-simulation-academic-ranks';
import type { RegimeGroupValidationDto } from 'api/generated/schemas';

type ModelId = string | number;
type RegimeId = string | number;
type GroupId = string | number;
type ClientId = string | number;

interface UseModelScopedParams {
  userId: string;
  modelId: ModelId;
  enabled?: boolean;
}

interface CreateRegimeVars {
  userId: string;
  modelId: ModelId;
  data: unknown;
}

interface UpdateGroupVars {
  userId: string;
  modelId: ModelId;
  groupId: GroupId;
  data: unknown;
}

interface DeleteGroupVars {
  userId: string;
  modelId: ModelId;
  groupId: GroupId;
}

interface UpdateRegimeVars {
  userId: string;
  modelId: ModelId;
  regimeId: RegimeId;
  data: unknown;
}

interface DeleteRegimeVars {
  userId: string;
  modelId: ModelId;
  regimeId: RegimeId;
}

interface CopyRegimeVars {
  userId: string;
  modelId: ModelId;
  regimeId: RegimeId;
  data: unknown;
}

interface UseAcademicRanksParams {
  userId: string;
  aidModelId: ModelId;
  enabled?: boolean;
}

interface ValidateRegimeGroupVars {
  userId: string;
  modelId: ModelId;
  groupId: GroupId;
  enabled?: boolean;
}

interface UseCustomLogicParams {
  userId: string;
  clientId: ClientId;
  enabled?: boolean;
}

export const regimeKeys = {
  all: ['regimes'] as const,
  model: (modelId: ModelId): readonly unknown[] => [
    ...regimeKeys.all,
    'model',
    modelId,
  ],
};

export const regimeGroupKeys = {
  all: ['regimeGroups'] as const,
  model: (modelId: ModelId): readonly unknown[] => [
    ...regimeGroupKeys.all,
    'model',
    modelId,
  ],
};

export const regimeGroupValidationKeys = {
  all: ['regimeGroupValidation'] as const,
  group: (modelId: ModelId, groupId: GroupId): readonly unknown[] => [
    ...regimeGroupValidationKeys.all,
    'model',
    modelId,
    'group',
    groupId,
  ],
};

// The comparison query key is a single literal URL string with no
// array-prefix relationship to regimeKeys/regimeGroupKeys (see
// getGetRegimeComparisonQueryKey once regenerated), and a regime-group edit
// can shift metrics across several regimes at once without saying which
// ones - so "invalidate every regime's comparison cache for this model" has
// to be a predicate match on the URL string rather than an exact queryKey
// match.
const invalidateRegimeComparisonQueries = (
  queryClient: QueryClient,
  userId: string,
  modelId: ModelId
): Promise<void> => {
  const prefix = `/api/${userId}/aid-models/${modelId}/regimes/`;
  const suffix = '/comparison';

  return queryClient.invalidateQueries({
    predicate: (query) => {
      const key = query.queryKey[0];

      return (
        typeof key === 'string' &&
        key.startsWith(prefix) &&
        key.endsWith(suffix)
      );
    },
  });
};

const invalidateRegimeQueries = (
  queryClient: QueryClient,
  userId: string,
  modelId: ModelId
): void => {
  queryClient.invalidateQueries({ queryKey: regimeKeys.all });
  queryClient.invalidateQueries({ queryKey: regimeGroupKeys.all });
  invalidateRegimeComparisonQueries(queryClient, userId, modelId);
};

// Adding or deleting a regime can change which students fall into a group's
// population, so the group-validation result must be refetched. Renaming or
// duplicating a regime keeps the population set unchanged, so those mutations
// keep using `invalidateRegimeQueries` and skip the validation refetch.
const invalidateRegimeAndValidationQueries = (
  queryClient: QueryClient,
  userId: string,
  modelId: ModelId
): void => {
  invalidateRegimeQueries(queryClient, userId, modelId);
  queryClient.invalidateQueries({ queryKey: regimeGroupValidationKeys.all });
};

export const useRegimes = ({
  userId,
  modelId,
  enabled = true,
}: UseModelScopedParams) =>
  useQuery({
    queryKey: regimeKeys.model(modelId),
    queryFn: () => getRegimes(userId, modelId),
    enabled: enabled && !!userId && !!modelId,
  });

export const useRegimeGroups = ({
  userId,
  modelId,
  enabled = true,
}: UseModelScopedParams) =>
  useQuery({
    queryKey: regimeGroupKeys.model(modelId),
    queryFn: () => getRegimeGroups(userId, modelId),
    enabled: enabled && !!userId && !!modelId,
  });

export const useCreateRegime = () => {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: ({ userId, modelId, data }: CreateRegimeVars) =>
      createRegime(userId, modelId, data),
    onSuccess: (_data, { userId, modelId }) =>
      invalidateRegimeAndValidationQueries(queryClient, userId, modelId),
  });
};

export const useCreateRegimeGroup = () => {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: ({ userId, modelId, data }: CreateRegimeVars) =>
      createRegimeGroup(userId, modelId, data),
    onSuccess: (_data, { userId, modelId }) =>
      invalidateRegimeQueries(queryClient, userId, modelId),
  });
};

export const useUpdateRegimeGroup = () => {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: ({ userId, modelId, groupId, data }: UpdateGroupVars) =>
      updateRegimeGroup(userId, modelId, groupId, data),
    onSuccess: (_data, { userId, modelId }) =>
      invalidateRegimeQueries(queryClient, userId, modelId),
  });
};

export const useDeleteRegimeGroup = () => {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: ({ userId, modelId, groupId }: DeleteGroupVars) =>
      deleteRegimeGroup(userId, modelId, groupId),
    onSuccess: (_data, { userId, modelId }) =>
      invalidateRegimeQueries(queryClient, userId, modelId),
  });
};

export const useUpdateRegime = () => {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: ({ userId, modelId, regimeId, data }: UpdateRegimeVars) =>
      updateRegime(userId, modelId, regimeId, data),
    onSuccess: (_data, { userId, modelId }) =>
      invalidateRegimeQueries(queryClient, userId, modelId),
  });
};

export const useDeleteRegime = () => {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: ({ userId, modelId, regimeId }: DeleteRegimeVars) =>
      deleteRegime(userId, modelId, regimeId),
    onSuccess: (_data, { userId, modelId }) =>
      invalidateRegimeAndValidationQueries(queryClient, userId, modelId),
  });
};

export const useCopyRegime = () => {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: ({ userId, modelId, regimeId, data }: CopyRegimeVars) =>
      copyRegime(userId, modelId, regimeId, data),
    onSuccess: (_data, { userId, modelId }) =>
      invalidateRegimeQueries(queryClient, userId, modelId),
  });
};

export const useInvalidateRegimeComparison = () => {
  const queryClient = useQueryClient();

  return useCallback(
    (userId: string, modelId: ModelId) =>
      invalidateRegimeComparisonQueries(queryClient, userId, modelId),
    [queryClient]
  );
};

export const academicRankKeys = {
  all: ['academicRanks'] as const,
  model: (aidModelId: ModelId): readonly unknown[] => [
    ...academicRankKeys.all,
    'model',
    aidModelId,
  ],
};

export const useAcademicRanks = ({
  userId,
  aidModelId,
  enabled = true,
}: UseAcademicRanksParams) =>
  useQuery({
    queryKey: academicRankKeys.model(aidModelId),
    queryFn: () => getAcademicRanks(userId, Number(aidModelId)),
    enabled: enabled && !!userId && !!aidModelId,
    staleTime: 1000 * 60 * 10,
  });

export const customLogicKeys = {
  all: ['customLogic'] as const,
  client: (clientId: ClientId): readonly unknown[] => [
    ...customLogicKeys.all,
    'client',
    clientId,
  ],
};

export const useCustomLogic = ({
  userId,
  clientId,
  enabled = true,
}: UseCustomLogicParams) =>
  useQuery({
    queryKey: customLogicKeys.client(clientId),
    queryFn: () => getCustomLogic(userId, clientId),
    enabled: enabled && !!userId && !!clientId,
    staleTime: 1000 * 60 * 30,
  });

export const useValidateRegimeGroup = ({
  userId,
  modelId,
  groupId,
  enabled = true,
}: ValidateRegimeGroupVars) =>
  useQuery<RegimeGroupValidationDto>({
    queryKey: regimeGroupValidationKeys.group(modelId, groupId),
    queryFn: () =>
      validateRegimeGroup(
        userId,
        modelId,
        groupId
      ) as Promise<RegimeGroupValidationDto>,
    enabled: enabled && !!userId && !!modelId && !!groupId,
    retry: false,
  });
